import { FaroMenuItem } from "@components/common/faro-select/faro-menu-item";
import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import {
  getRoleMenuItemSx,
  RoleMenuItem,
} from "@components/role/role-menu-item";
import { SdbMemberTypes } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { MenuItem, UserRoleIcon } from "@flat-ui";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";
import { getDisabledRoles } from "@utils/workspace-role-utils";

interface Props {
  /** The member whose role can be changed */
  member: SdbMemberTypes;

  /** Company ID */
  companyId: string;

  /** Context in which the members table is used */
  subjectType: MemberTableSubject;

  /** The selected project (for project member tables) */
  selectedProject?: SdbProject | null;

  /** The selected group (for group member tables) */
  group?: FaroCoreApiTypes.GroupDetailResponse | null;

  /** Callback to close the parent 3-dot menu */
  onMenuClose: () => void;
}

/**
 * Menu button that opens a role change sub-menu with assignable roles.
 * Uses the same FaroMenuItem + RoleMenuItem pattern as the bulk action buttons.
 */
export function MemberRoleMenuButton({
  member,
  companyId,
  subjectType,
  selectedProject,
  group,
  onMenuClose,
}: Props): JSX.Element {
  const { changeMemberRole } = useChangeMemberRole({
    companyId,
    subjectType,
    memberIdentity: member.identity,
    currentRole: member.role,
    selectedProject,
    group,
    location: "members-role-menu-button",
  });

  const { roles, disabledRoles } = getAssignableRoles(subjectType, member.role);

  async function onRoleClicked(role: string): Promise<void> {
    try {
      await changeMemberRole(role);
    } catch {
      // Errors are handled by the respective slices
    }
    onMenuClose();
  }

  return (
    <MenuItem label="Change role" Icon={UserRoleIcon} subMenuTrigger="click">
      {roles.map((role) => (
        <FaroMenuItem
          key={role}
          value={role}
          disabled={disabledRoles.includes(role)}
          onClick={() => onRoleClicked(role)}
          sx={getRoleMenuItemSx({ isTableCell: false })}
        >
          <RoleMenuItem role={role} isRoleSelected={role === member.role} />
        </FaroMenuItem>
      ))}
    </MenuItem>
  );
}

interface AssignableRolesResult {
  roles: (FaroCoreApiTypes.CompanyRole | FaroCoreApiTypes.ProjectRole)[];
  disabledRoles: (
    FaroCoreApiTypes.CompanyRole | FaroCoreApiTypes.ProjectRole
  )[];
}

/** Returns the roles and disabled roles matching the table column dropdown */
function getAssignableRoles(
  subjectType: MemberTableSubject,
  currentRole: string
): AssignableRolesResult {
  switch (subjectType) {
    case "project":
      return {
        roles: FaroCoreApiConst.allProjectRoles,
        disabledRoles: ["RESOURCE_OWNER"],
      };

    case "group":
      return {
        roles: FaroCoreApiConst.allGroupRoles,
        disabledRoles: [],
      };

    case "workspace":
    case "team":
      return {
        roles: FaroCoreApiConst.allCompanyRoles,
        disabledRoles: getDisabledRoles(
          currentRole as FaroCoreApiTypes.CompanyRole
        ),
      };

    default:
      return { roles: [], disabledRoles: [] };
  }
}
